import { useCallback, useEffect, useRef, useState } from 'react';
import { getPassportKioskScanState } from '../utils/passportKioskApi';

/** Desk toggles scan on another device — light poll only (was 2s; that flooded the API). */
const POLL_MS = 12000;

/**
 * Passport scan gate for kiosk pages.
 * @param {boolean} active — when false, skips fetch and reports scanEnabled false.
 */
export function usePassportKioskScanGate(active) {
  const [scanEnabled, setScanEnabled] = useState(false);
  const [idleVideoUrl, setIdleVideoUrl] = useState('');
  const [idleVideoKey, setIdleVideoKey] = useState('');
  const [ready, setReady] = useState(!active);
  const idleVideoKeyRef = useRef('');

  const refresh = useCallback(async () => {
    if (!active) {
      idleVideoKeyRef.current = '';
      setIdleVideoKey('');
      setScanEnabled(false);
      setIdleVideoUrl('');
      setReady(true);
      return;
    }
    try {
      const data = await getPassportKioskScanState();
      setScanEnabled(Boolean(data?.scan_enabled));
      const key = String(data?.idle_video_key || '').trim();
      const url = String(data?.idle_video_url || '').trim();
      if (!key) {
        idleVideoKeyRef.current = '';
        setIdleVideoKey('');
        setIdleVideoUrl('');
      } else if (key !== idleVideoKeyRef.current) {
        idleVideoKeyRef.current = key;
        setIdleVideoKey(key);
        setIdleVideoUrl(url);
      } else {
        setIdleVideoKey(key);
        // Refresh signed object-store URL each poll (X-Amz-Expires is typically 1h).
        if (url) setIdleVideoUrl(url);
      }
    } catch {
      setScanEnabled(false);
    } finally {
      setReady(true);
    }
  }, [active]);

  useEffect(() => {
    if (!active) {
      idleVideoKeyRef.current = '';
      setIdleVideoKey('');
      setScanEnabled(false);
      setIdleVideoUrl('');
      setReady(true);
      return undefined;
    }

    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      await refresh();
    };

    void tick();
    const timer = setInterval(tick, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [active, refresh]);

  return { scanEnabled, idleVideoUrl, idleVideoKey, ready, refresh };
}
